"use client";

import { useState } from "react";
import { Bot, Trash2 } from "lucide-react";
import { ErrorNotice } from "@/components/ui";
import { AccountCard } from "@/components/account-card";
import { toast } from "@/components/toast-provider";
import { errorMessage, mutation } from "@/lib/api";
import { useResource } from "@/lib/use-resource";

export const ASSISTANT_READ_LOG_KEY = "velnqo.assistant.readLog";

export function readLogPreference() {
  return typeof window === "undefined" || window.localStorage.getItem(ASSISTANT_READ_LOG_KEY) !== "0";
}

export function AssistantPreferences() {
  const status = useResource<{ available: boolean; allowLogs: boolean; collectFeedback: boolean; trainingOptOut: boolean }>("/assistant");
  const [readLog, setReadLog] = useState(readLogPreference);
  const [error, setError] = useState("");

  if (!status.data?.available) return null;

  const toggleLog = (value: boolean) => {
    setReadLog(value);
    window.localStorage.setItem(ASSISTANT_READ_LOG_KEY, value ? "1" : "0");
  };
  const setOptOut = async (value: boolean) => {
    try {
      await mutation("/assistant/preferences", { trainingOptOut: value }, "PUT");
      status.refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };
  const deleteHistory = async () => {
    try {
      await mutation("/assistant/history", {}, "DELETE");
      toast({ tone: "success", title: "Assistant history deleted" });
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  return (
    <AccountCard icon={<Bot size={18} />} title="Assistant" description="Control what the assistant can see and how your feedback is used.">
      {error && <ErrorNotice message={error} />}
      <div className="account-list">
        {status.data.allowLogs && (
          <label className="account-row account-toggle">
            <input type="checkbox" checked={readLog} onChange={(event) => toggleLog(event.target.checked)} />
            <span className="account-row-main"><strong>Read my latest log</strong><span className="account-muted">Lets the assistant look at recent console output to diagnose problems.</span></span>
          </label>
        )}
        {status.data.collectFeedback && (
          <label className="account-row account-toggle">
            <input type="checkbox" checked={!status.data.trainingOptOut} onChange={(event) => void setOptOut(!event.target.checked)} />
            <span className="account-row-main"><strong>Help improve the assistant</strong><span className="account-muted">Use my rated answers for training. Personal details are removed first.</span></span>
          </label>
        )}
        <button className="button" onClick={() => void deleteHistory()}><Trash2 size={14} /> Delete my assistant history</button>
      </div>
    </AccountCard>
  );
}
